import { useEffect, useState } from 'react';
import Mousetrap from 'mousetrap';

let counter = 0;

const registry = {
  keyup: {},
  keydown: {},
  keypress: {},
};

function updateCallbacks(event, hotkey) {
  const callbacks = registry[event][hotkey];
  const list = Object.keys(callbacks).filter(k => k !== '__order__');
  list.sort((a, b) => {
    const ca = callbacks[a];
    const cb = callbacks[b];
    if (ca.priority === cb.priority) {
      return parseInt(a, 10) - parseInt(b, 10);
    }
    return cb.priority - ca.priority;
  });
  callbacks['__order__'] = list;
}

function globalHandler(e, hotkey) {
  // This is so if the browser has the same hotkey as one we want to use, the
  // browser's hotkey action won't fire.
  e.preventDefault();

  const callbacks = registry[e.type][hotkey];
  if (!('__order__' in callbacks)) {
    updateCallbacks(e.type, hotkey);
  }
  const list = callbacks['__order__'].map(c => callbacks[c]);
  for (const c of list) {
    c.callback(e);
    if (c.stop) break;
  }
}

function _register({ id, hotkey, callback, priority, event, stop }) {
  hotkey = hotkey.toLowerCase(); // Mousetrap lowercases key names
  if (!(hotkey in registry[event])) {
    registry[event][hotkey] = {};
    Mousetrap.bind(hotkey, globalHandler, event);
  }
  registry[event][hotkey][id] = {
    hotkey,
    callback,
    priority,
    stop,
  };
  delete registry[event][hotkey]['__order__'];
}

function _unregister({ event, hotkey, id }) {
  hotkey = hotkey.toLowerCase(); // Mousetrap lowercases key names
  delete registry[event][hotkey][id];
  delete registry[event][hotkey]['__order__'];
  if (Object.keys(registry[event][hotkey]).length === 0) {
    Mousetrap.unbind(hotkey, globalHandler, event);
    delete registry[event][hotkey];
  }
}

// Hook for functional components
const useGlobalHotkey = ({
  hotkey,
  hotkeys,
  callback,
  priority,
  event,
  stop,
}) => {
  if (priority === undefined) priority = 0;
  if (event === undefined) event = 'keyup';
  const [id] = useState(counter++);

  useEffect(() => {
    //console.log(`in effect for ${id}`);
    if (hotkeys) {
      hotkeys.forEach(h =>
        _register({ id, hotkey: h, callback, priority, event, stop }),
      );
    } else if (hotkey) {
      _register({ id, hotkey, callback, priority, event, stop });
    }
    return () => {
      if (hotkeys) {
        hotkeys.forEach(h => _unregister({ event, hotkey: h, id }));
      } else if (hotkey) {
        _unregister({ event, hotkey, id });
      }
    };
  }, [id, callback, hotkey, hotkeys, priority, event, stop]);
};

// These next two functions are for Class components:
// Call registerHotKey from componentDidMount and store the returned id in the
// component's state. Call unregisterHotkey from componentWillUnmount.
export function registerHotkey({
  hotkey,
  hotkeys,
  callback,
  priority,
  event,
  stop,
}) {
  if (priority === undefined) priority = 0;
  if (event === undefined) event = 'keyup';
  const id = counter++;

  if (hotkeys) {
    for (const h of hotkeys) {
      _register({ id, hotkey: h, callback, priority, event, stop });
    }
  } else {
    _register({ id, hotkey, callback, priority, event, stop });
  }
  return id;
}

export function unregisterHotkey(id) {
  for (const event of Object.keys(registry)) {
    for (const hotkey of Object.keys(registry[event])) {
      if (id in registry[event][hotkey]) {
        _unregister({ id, event, hotkey });
      }
    }
  }
}

export default useGlobalHotkey;
